/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, PrimaryButton } from '@fluentui/react/lib/Button';
import { Link } from '@fluentui/react/lib/Link';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { MigrationModalFooter } from 'src/verticals/azureMigrationModal/newComponents/MigrationModalFooter';

export type IntroductionScreenProps = {
    /**
     * True if the modal is still loading apps
     * required for viewing.
     */
    isInitializing: boolean;

    /**
     * User agreed to continue in the migration wizard.
     */
    onContinue: () => void;
};

const messages = defineMessages({
    title: {
        id: 'multiRegionMigrationModal.introductionScreen.title',
        defaultMessage: 'Azure Migration'
    },
    bodyPart1: {
        id: 'multiRegionMigrationModal.introductionScreen.bodyPart1',
        defaultMessage: `Starting 12/01/2020, Language Understanding (LUIS) will require an Azure
resource in an Azure subscription. In order to continue to have access to your LUIS apps, you need to
migrate them to an Azure resource. This will walk you through the steps to migrate your apps to Azure.`
    },
    bodyPart2: {
        id: 'multiRegionMigrationModal.introductionScreen.bodyPart2',
        defaultMessage: `Your key string will not be affected, therefore you can continue to use your
authoring key normally to consume any LUIS API.`
    },
    learnMoreLink: {
        id: 'multiRegionMigrationModal.introductionScreen.learnMoreLink',
        defaultMessage: 'Learn more about migrating to an authoring resource.'
    },
    createAzureSubscriptionButton: {
        id: 'multiRegionMigrationModal.createAzureSubscriptionScreen.createAzureSubscriptionButton',
        defaultMessage: 'Create a free Azure subscription'
    },
    bodyPart3: {
        id: 'multiRegionMigrationModal.introductionScreen.bodyPart3',
        defaultMessage: 'Already created your account?'
    },
    refreshLink: {
        id: 'multiRegionMigrationModal.introductionScreen.refreshLink',
        defaultMessage: 'Refresh the page to update'
    },
    loading: {
        id: 'multiRegionMigrationModal.introductionScreen.loading',
        defaultMessage: 'Checking your Azure account ...'
    }
});

const createSubscriptionButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { width: '270px' } });

export const startScreenTitle = messages.title;

export const IntroductionScreen = observer((props: IntroductionScreenProps) => {
    const { isInitializing, onContinue } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const areSubscriptionsLoading = rootStore.azureStore.subscriptionStoreHouse.tracker.isRunning;
    const doesSubscriptionExist = rootStore.azureStore.subscriptionStoreHouse.items.length > 0;

    return (
        <Stack verticalFill tokens={{ childrenGap: 40 }}>
            <Stack verticalFill tokens={{ childrenGap: 40 }}>
                <Stack tokens={{ childrenGap: 16 }}>
                    <LuisText variant="xLarge" fontWeight={600}>
                        {localize(messages.title)}
                    </LuisText>

                    <LuisText>{localize(messages.bodyPart1)}</LuisText>

                    <LuisText>{localize(messages.bodyPart2)}</LuisText>

                    <ExternalLink inNewTab href="https://docs.microsoft.com/azure/cognitive-services/luis/luis-migration-authoring">
                        {localize(messages.learnMoreLink)}
                    </ExternalLink>
                </Stack>

                {!areSubscriptionsLoading && !doesSubscriptionExist && (
                    <Stack tokens={{ childrenGap: 16 }}>
                        <PrimaryButton styles={createSubscriptionButtonStyles} href="https://azure.microsoft.com/free/" target="_blank">
                            {localize(messages.createAzureSubscriptionButton)}
                        </PrimaryButton>

                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            <LuisText>{localize(messages.bodyPart3)}</LuisText>

                            <Link onClick={() => window.location.reload()}>{localize(messages.refreshLink)}</Link>
                        </Stack>
                    </Stack>
                )}

                {(isInitializing || areSubscriptionsLoading) && (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <Spinner size={SpinnerSize.small} />
                        <LuisText fontWeight={500}>{localize(messages.loading)}</LuisText>
                    </Stack>
                )}
            </Stack>

            <MigrationModalFooter backDisabled nextDisabled={!doesSubscriptionExist || isInitializing} onContinue={onContinue} />
        </Stack>
    );
});
